import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { TranslationService } from '../../../core/services/translation.service';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { isSupportedLanguage } from '../../../core/services/translation.service';

@Component({
  selector: 'app-language-selector',
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    MatButtonModule
  ],
  template: `
    <button mat-button [matMenuTriggerFor]="languageMenu" class="lang-button">
      <img src="{{ getLanguageByCode(currentLang).image }}" alt="{{ getLanguageByCode(currentLang).name }}">
      <span>{{ getLanguageByCode(currentLang).short }}</span>
    </button>
    <mat-menu #languageMenu="matMenu" class="lang-menu">
      @for (lang of languages; track lang.code) {
        <button mat-menu-item (click)="onLanguageChange(lang.code)">
          <img src="{{ lang.image }}" alt="{{ lang.name }}">
          <span>{{ lang.name }}</span>
        </button>
      }
    </mat-menu>
  `,
  styles: [`
    .lang-button {
      ::ng-deep .mdc-button__label {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      span {
        color: #25516e;
      }
    }
    .lang-button img {
      width: 20px;
      height: 20px;
      border-radius: 20px;
      object-fit: cover;
      border: 1px solid rgba(0, 0, 0, 0.4);
    }
    ::ng-deep .mat-mdc-menu-item {
      img {
        width: 20px;
        height: 20px;
        border-radius: 20px;
        object-fit: cover;
        border: 1px solid rgba(0, 0, 0, 0.2);
      }
      .mat-mdc-menu-item-text {
        display: flex;
        align-items: center;
        flex-direction: row;
        gap: 8px;
      }
    }
  `]
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  languages: { code: string, name: string, short: string, image: string }[];
  currentLang = 'en';
  private destroy$ = new Subject<void>();

  constructor(private translationService: TranslationService, private router: Router) {
    this.languages = this.translationService.getAvailableLanguages();
  }

  getLanguageByCode(langCode: string): { code: string, name: string, short: string, image: string } {
    return this.languages.find(lang => lang.code === langCode) || { code: 'en', name: 'English', short: 'Eng', image: 'assets/icons/lang/us.svg' };
  }

  ngOnInit(): void {
    this.translationService.getCurrentLang().pipe(takeUntil(this.destroy$)).subscribe(lang => {
      this.currentLang = lang;
    });
  }

  onLanguageChange(newLang: string): void {
    const currentUrl = this.router.url;
    const pathParts = currentUrl.split('/').filter(Boolean);
    // Remove existing language prefix if supported
    if (isSupportedLanguage(pathParts[0])) {
      pathParts.shift();
    }
    let newPath = '';

    if (newLang !== 'en') {
      newPath = `/${newLang}/${pathParts.join('/')}`;
    } else {
      newPath = `/${pathParts.join('/')}`;
    }
    // trim last slash
    newPath = newPath.replace(/\/$/, '');

    // Update translation state
    this.translationService.loadTranslations(isSupportedLanguage(newLang) ? newLang : 'en');

    // Save selected lang
    this.currentLang = newLang;

    // Navigate
    this.router.navigateByUrl(newPath || '/');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
